import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import { useTranslation } from '../../hooks/useTranslation';

const SectionWithMockup = ({
    title,
    description,
    primaryImageSrc,
    secondaryImageSrc,
    tertiaryImageSrc,
    reverseLayout = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const containerVariants = {
        hidden: {},
        visible: {
             transition: {
                staggerChildren: 0.2,
            }
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
    };

    const layoutClasses = reverseLayout
        ? "md:grid-cols-2 md:grid-flow-col-dense"
        : "md:grid-cols-2";

    const textOrderClass = reverseLayout ? "md:col-start-2" : "";
    const imageOrderClass = reverseLayout ? "md:col-start-1" : "";


    return (
        <section className="relative py-24 md:py-48 bg-black overflow-hidden">
            <div className="container max-w-[1220px] w-full px-6 md:px-10 relative z-10 mx-auto">
                <motion.div
                     className={`grid grid-cols-1 gap-16 md:gap-8 w-full items-center ${layoutClasses}`}
                     variants={containerVariants}
                     initial="hidden"
                     whileInView="visible"
                     viewport={{ once: true, amount: 0.2 }}
                >
                    {/* Text Content */}
                    <motion.div
                        className={`flex flex-col items-start gap-4 mt-10 md:mt-0 max-w-[546px] mx-auto md:mx-0 ${textOrderClass}`}
                        variants={itemVariants}
                    >
                         <div className="space-y-2 md:space-y-1">
                            <h2 className="text-white text-3xl md:text-[40px] font-semibold leading-tight md:leading-[53px]">
                                {title}
                            </h2>
                        </div>

                        <p className="text-[#868f97] text-sm md:text-[15px] leading-6">
                            {description}
                        </p>
                    </motion.div>

                    {/* App mockup/Image Content */}
                    <motion.div
                        className={`relative mt-10 md:mt-0 mx-auto ${imageOrderClass} w-full max-w-[300px] md:max-w-[471px]`}
                        variants={itemVariants}
                    >
                        {/* Tertiary Image - Background Element (optional) */}
                        {tertiaryImageSrc && (
                            <motion.div
                                className={`absolute w-[200px] h-[250px] md:w-[320px] md:h-[400px] bg-[#090909] rounded-[32px] z-0 pointer-events-none`}
                                style={{
                                    top: '50%',
                                    left: reverseLayout ? '10%' : 'auto',
                                    right: reverseLayout ? 'auto' : '10%',
                                    transform: 'translateY(-50%)',
                                    filter: 'blur(1px)'
                                }}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 1.0, ease: "easeOut", delay: 0.2 }}
                                viewport={{ once: true, amount: 0.5 }}
                            >
                                <div
                                    className="relative w-full h-full bg-cover bg-center rounded-[32px]"
                                    style={{
                                        backgroundImage: `url(${tertiaryImageSrc})`,
                                    }}
                                />
                            </motion.div>
                        )}

                        {/* Decorative Background Element */}
                        <motion.div
                            className={`absolute w-[300px] h-[317px] md:w-[472px] md:h-[500px] bg-[#090909] rounded-[32px] z-0 shadow-2xl pointer-events-none`}
                             style={{
                                top: reverseLayout ? 'auto' : '10%',
                                bottom: reverseLayout ? '10%' : 'auto',
                                left: reverseLayout ? 'auto' : '-20%',
                                right: reverseLayout ? '-20%' : 'auto',
                                transform: reverseLayout ? 'translate(0, 0)' : 'translateY(10%)',
                                filter: 'blur(2px)',
                                backgroundImage: `url(${isHovered ? primaryImageSrc : secondaryImageSrc})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                boxShadow: isHovered ? '0px 10px 30px rgba(0, 255, 128, 0.5)' : '0px 5px 15px rgba(0, 0, 0, 0.3)',
                                transition: 'box-shadow 0.5s ease-in-out',
                            }}
                            initial={{ y: reverseLayout ? 0 : 0, scale: 1 }}
                            whileInView={{ y: reverseLayout ? -20 : -30, scale: isHovered ? 1.05 : 1 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                            viewport={{ once: true, amount: 0.5 }}
                        />

                        {/* Main Mockup Card */}
                        <motion.div
                            className="relative w-full h-[405px] md:h-[637px] bg-[#ffffff0a] rounded-[32px] backdrop-blur-[15px] backdrop-brightness-[100%] border-0 z-10 overflow-hidden cursor-pointer"
                            initial={{ y: reverseLayout ? 0 : 0 }}
                            whileInView={{ y: reverseLayout ? 20 : 30 }}
                            transition={{ duration: 1.2, ease: "easeOut", delay: 0.1 }}
                            viewport={{ once: true, amount: 0.5 }}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            <div className="p-0 h-full">
                                <div
                                    className="h-full relative"
                                    style={{
                                        backgroundSize: "100% 100%",
                                    }}
                                >
                                    {/* Primary Image */}
                                    <motion.div
                                        className="w-full h-full bg-cover bg-center absolute inset-0"
                                        style={{
                                            backgroundImage: `url(${primaryImageSrc})`,
                                        }}
                                        animate={{ opacity: isHovered ? 0 : 1 }}
                                        transition={{ duration: 0.5, ease: "easeInOut" }}
                                    />
                                    {/* Secondary Image - shown on hover */}
                                    <motion.div
                                        className="w-full h-full bg-cover bg-center absolute inset-0"
                                        style={{
                                            backgroundImage: `url(${secondaryImageSrc})`,
                                        }}
                                        animate={{ opacity: isHovered ? 1 : 0 }}
                                        transition={{ duration: 0.5, ease: "easeInOut" }}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>

            {/* Decorative bottom gradient */}
            <div
                className="absolute w-full h-px bottom-0 left-0 z-0"
                style={{
                    background:
                        "radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 100%)",
                }}
            />

            {/* Additional Text Below the Section */}
            <div className="mt-56 text-center px-6 md:px-10 max-w-6xl w-11/12 mx-auto">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                    {t('sectionTitle')}
                </h3>
                <p className="text-lg text-slate-400 leading-relaxed">
                    {t('sectionDescription')}
                </p>
            </div>

            {/* Performance Comparison Section */}
        
            {/* Enhanced Metrics Section with Aura Effects */}
            <div className="mt-16 text-center px-6 md:px-10">
                <motion.h3 
                    className="text-2xl md:text-3xl font-bold text-white mb-6"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    {t('performanceTitle')}
                </motion.h3>
                <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ staggerChildren: 0.2 }}
                >
                    <motion.div 
                        className="bg-[#090909] rounded-lg p-6 shadow-lg transform hover:scale-105 hover:shadow-[0_0_20px_rgba(0,255,128,0.7)] transition-transform duration-300"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <h4 className="text-xl font-semibold text-emerald-400 mb-4">{t('aiStandard')}</h4>
                        <p className="text-lg text-slate-400">{t('aiStandardDesc')}</p>
                    </motion.div>
                    <motion.div 
                        className="bg-[#090909] rounded-lg p-6 shadow-lg transform hover:scale-105 hover:shadow-[0_0_20px_rgba(0,255,128,0.7)] transition-transform duration-300"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <h4 className="text-xl font-semibold text-emerald-400 mb-4">{t('HiperyonAI')}</h4>
                        <p className="text-lg text-slate-400">{t('HiperyonAIDesc')}</p>
                    </motion.div>
                </motion.div>
                <motion.p 
                    className="text-lg text-slate-400 mt-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    {t('performanceConclusion')}
                </motion.p>
                <motion.h3 
                    className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    {t('metricsTitle')}
                </motion.h3>
                <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ staggerChildren: 0.2 }}
                >
                    <motion.div 
                        className="bg-[#090909] rounded-lg p-6 shadow-lg transform hover:scale-105 hover:shadow-[0_0_20px_rgba(0,255,128,0.7)] transition-transform duration-300"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <h4 className="text-xl font-semibold text-emerald-400 mb-4">{t('metric1Title')}</h4>
                        <p className="text-lg text-slate-400">{t('metric1Value')}</p>
                    </motion.div>
                    <motion.div 
                        className="bg-[#090909] rounded-lg p-6 shadow-lg transform hover:scale-105 hover:shadow-[0_0_20px_rgba(0,255,128,0.7)] transition-transform duration-300"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <h4 className="text-xl font-semibold text-emerald-400 mb-4">{t('metric2Title')}</h4>
                        <p className="text-lg text-slate-400">{t('metric2Value')}</p>
                    </motion.div>
                    <motion.div 
                        className="bg-[#090909] rounded-lg p-6 shadow-lg transform hover:scale-105 hover:shadow-[0_0_20px_rgba(0,255,128,0.7)] transition-transform duration-300"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <h4 className="text-xl font-semibold text-emerald-400 mb-4">{t('metric3Title')}</h4>
                        <p className="text-lg text-slate-400">{t('metric3Value')}</p>
                    </motion.div>
                    <motion.div 
                        className="bg-[#090909] rounded-lg p-6 shadow-lg transform hover:scale-105 hover:shadow-[0_0_20px_rgba(0,255,128,0.7)] transition-transform duration-300"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <h4 className="text-xl font-semibold text-emerald-400 mb-4">{t('metric4Title')}</h4>
                        <p className="text-lg text-slate-400">{t('metric4Value')}</p>
                    </motion.div>
                </motion.div>
                <motion.p 
                    className="text-2xl md:text-3xl font-bold text-white mt-32 mb-32 leading-relaxed max-w-full ml-0 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    {t('metricsConclusion')}
                </motion.p>
            </div>

            {/* Call-to-Action Section */}
            <div className="mt-16 text-center px-6 md:px-10">
                <div className="bg-[#090909] rounded-lg p-8 shadow-lg">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                        {t('ctaTitle')}
                    </h3>
                    <button
                        type="button"
                        onClick={() => { console.log('CTA clicked - navigating to /signup'); navigate('/signup'); }}
                        className="relative z-50 pointer-events-auto px-6 py-3 rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-400 transition"
                        aria-label={t('ctaButton')}
                    >
                        {t('ctaButton')}
                    </button>
                </div>
            </div>
        </section>
    );
};


export default SectionWithMockup;